import { GetStaticProps } from 'next';
import Link from 'next/link';
import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

interface Props {
  isAuthenticated: boolean;
}

const CharlieMarshallCaseStudy = ({ isAuthenticated }: Props) => {
  return (
    <LandingPageLayout
      title="Charlie Marshall Elementary Impact Story | Curipod"
      subTitle="How intentional leadership and Curipod drove writing growth across Charlie Marshall Elementary in Aransas Pass ISD, Texas — with up to 43% ECR score gains."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>CASE STUDY</HeroTag>
            <HeroTitle>Five Steps, Real Results</HeroTitle>
            <HeroSubtitle>Charlie Marshall Elementary Impact Story</HeroSubtitle>
            <HeroDescription>
              How Principal Misty Del Angel&apos;s intentional rollout of Curipod
              transformed writing instruction across Charlie Marshall Elementary in
              Aransas Pass ISD, southeast Texas&nbsp;&mdash; turning struggling writers
              into confident learners.
            </HeroDescription>
            <HeroMeta>
              <MetaBadge>Aransas Pass ISD</MetaBadge>
              <MetaBadge>3rd &amp; 4th Grade ELAR</MetaBadge>
              <MetaBadge>2025–2026 School Year</MetaBadge>
            </HeroMeta>
            <HeroCtaWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <HeroCtaButton type="primary">Sign up for free</HeroCtaButton>
                </a>
              </Link>
            </HeroCtaWrapper>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem>
                  <StatNumber>
                    30<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>ECR Growth (Score&nbsp;2+)</StatLabel>
                  <StatYear>3rd Grade (a)</StatYear>
                </StatItem>
                <ArrowContainer>&middot;</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    43<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>ECR Growth (Score&nbsp;2+)</StatLabel>
                  <StatYear>3rd Grade (b)</StatYear>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    25<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>ECR Growth (Score&nbsp;2+)</StatLabel>
                  <StatYear>4th Grade</StatYear>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* DOWNLOAD CTA (below hero) */}
      <DownloadCtaBanner>
        <MaxWidthContainer>
          <DownloadCtaInner>
            <DownloadCtaLabel>
              Want the full story? Download the PDF version of this case study.
            </DownloadCtaLabel>
            <a
              href="/Curipod_Case_Study_Charlie_Marshall_Elementary.pdf"
              download="Curipod_Case_Study_Charlie_Marshall_Elementary.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 44 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadCtaInner>
        </MaxWidthContainer>
      </DownloadCtaBanner>

      {/* THE CHALLENGE */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <TwoColumnGrid>
            <div>
              <SectionTag>FROM REFLECTION TO RESOLVE</SectionTag>
              <SectionTitle>A Campus Ready for Change</SectionTitle>
              <SectionText>
                Charlie Marshall Elementary in Aransas Pass ISD serves a community where
                nearly 75% of students qualify for free or reduced-price lunch. The campus
                had earned a D or F rating on state assessments for three consecutive
                years, and writing outcomes were a persistent challenge.
              </SectionText>
              <SectionText>
                When <strong>Misty Del Angel</strong> stepped into her role as principal,
                she knew exactly where to focus her energy. Teachers struggled to get kids
                to write&nbsp;&mdash; and even when they did, there was no practical way
                to provide meaningful feedback within a class period.
              </SectionText>
              <BlockQuote>
                <QuoteMark>&ldquo;</QuoteMark>
                <QuoteText>
                  If I&apos;m so passionate about writing, I need to be able to do
                  something to move my campus.
                </QuoteText>
                <QuoteAttrib>— Misty Del Angel, Principal</QuoteAttrib>
              </BlockQuote>
            </div>

            <TeamCard>
              <TeamCardTitle>The Charlie Marshall Team</TeamCardTitle>
              <TeamMemberList>
                <TeamMember>
                  <TeamRole>Principal</TeamRole>
                  <TeamName>Misty Del Angel</TeamName>
                  <TeamNote>Passionate about writing, led the Curipod rollout</TeamNote>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Campus</TeamRole>
                  <TeamName>Charlie Marshall Elementary</TeamName>
                  <TeamNote>Aransas Pass ISD, Southeast Texas</TeamNote>
                </TeamMember>
              </TeamMemberList>
              <FocusAreaGrid style={{ marginTop: 24 }}>
                {['Feedback', 'Leadership', 'Writing Instruction', 'Teacher Buy-In'].map(
                  area => (
                    <FocusAreaBadge key={area}>{area}</FocusAreaBadge>
                  ),
                )}
              </FocusAreaGrid>
            </TeamCard>
          </TwoColumnGrid>
        </MaxWidthContainer>
      </Section>

      {/* THE SHIFT */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              THE SHIFT THAT CHANGED EVERYTHING
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              Not a Mandate&nbsp;&mdash; A Roadmap
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              When Misty saw Curipod, the connection was immediate. Within months, nearly
              every teacher on campus was using Curipod with fidelity, and classrooms were
              already feeling different. But the impact didn&apos;t happen by
              accident&nbsp;&mdash; it came from how this school rolled Curipod out.
            </SectionText>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* FIVE STEPS */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <SectionTag style={{ display: 'block', textAlign: 'center' }}>
            IMPLEMENTATION ROADMAP
          </SectionTag>
          <SectionTitle style={{ textAlign: 'center' }}>
            Five Steps to Real Results
          </SectionTitle>
          <ImpactGrid>
            <ImpactCard>
              <ImpactIcon>🎯</ImpactIcon>
              <ImpactTitle>Step 1: Start Small to Scale Smart</ImpactTitle>
              <ImpactText>
                Misty didn&apos;t begin with a campus-wide rollout. She started with two
                ELAR teachers she trusted would see the value immediately. They became
                partners in the process&nbsp;&mdash; offering feedback, refining lessons,
                and building early momentum.
              </ImpactText>
              <ImpactQuote>
                &ldquo;I didn&apos;t just make a decision and roll something out. I said,
                &lsquo;I love this. I want to show it to you.&rsquo;&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Misty Del Angel</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>📚</ImpactIcon>
              <ImpactTitle>Step 2: Build on Existing Practices</ImpactTitle>
              <ImpactText>
                Curipod wasn&apos;t positioned as a replacement for the
                curriculum&nbsp;&mdash; it supplemented and strengthened it. Misty built
                lessons aligned to current units, Lexile levels, and pacing guides, giving
                teachers a clear starting point.
              </ImpactText>
              <ImpactQuote>
                &ldquo;First, we did the same passage all week like we did with our core
                curriculum… Since January, we&apos;re doing a new passage every
                day.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Misty Del Angel</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>📊</ImpactIcon>
              <ImpactTitle>Step 3: Let Data Create Believers</ImpactTitle>
              <ImpactText>
                Some of the strongest adopters weren&apos;t who Misty expected, including
                veteran teachers who had seen countless initiatives come and go. One
                teacher who &ldquo;always says, &lsquo;Please don&apos;t ask me to do
                anything with technology&rsquo;&rdquo; embraced Curipod fully.
              </ImpactText>
              <ImpactQuote>
                That classroom saw a 59% increase in students scoring a two or higher
                since September. &ldquo;She&apos;s a tracker. When she saw the growth,
                that&apos;s what did it.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Misty Del Angel</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>🤝</ImpactIcon>
              <ImpactTitle>Step 4: Normalize Support &amp; Vulnerability</ImpactTitle>
              <ImpactText>
                Misty didn&apos;t ask teachers to take risks alone. She modeled
                instruction, provided ready-to-use lessons, and encouraged
                curiosity&nbsp;&mdash; lowering the stakes so trying something new felt
                safe and supported.
              </ImpactText>
              <ImpactQuote>
                &ldquo;If they needed help, I would come teach the lesson so they could
                watch me.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Misty Del Angel</ImpactAttrib>
            </ImpactCard>

            <ImpactCard $wide>
              <ImpactIcon>⚖️</ImpactIcon>
              <ImpactTitle>Step 5: Balance Structure with Autonomy</ImpactTitle>
              <ImpactText>
                Most teachers began with Misty&apos;s lessons. Over time, they built their
                own. The focus moved from improving scores to instructional shifts.
              </ImpactText>
              <ImpactQuote>
                &ldquo;Once they saw the results themselves, that&apos;s where the shift
                happened.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Misty Del Angel</ImpactAttrib>
            </ImpactCard>
          </ImpactGrid>
        </MaxWidthContainer>
      </Section>

      {/* RESULTS */}
      <ResultsSection>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag
              style={{
                color: theme.colors.orange,
                display: 'block',
                textAlign: 'center',
              }}
            >
              RESULTS YOU CAN SEE AND FEEL
            </SectionTag>
            <SectionTitle style={{ color: theme.colors.white, textAlign: 'center' }}>
              ECR Growth Across Grades
            </SectionTitle>
            <SectionText
              style={{
                color: 'rgba(255,255,255,0.9)',
                textAlign: 'center',
                maxWidth: 720,
                margin: '0 auto 40px',
              }}
            >
              School data tracking of scores in Curipod as of January 2026 shows
              meaningful improvement in students scoring 2 or higher on Extended
              Constructed Responses across every grade level measured.
            </SectionText>

            <ResultsGrid>
              <ResultCard>
                <ResultGrade>3rd Grade (a)</ResultGrade>
                <ResultValue>
                  30<ResultPercent>%</ResultPercent>
                </ResultValue>
                <ResultMetric>ECR Growth (Score&nbsp;2+)</ResultMetric>
              </ResultCard>
              <ResultCard $highlight>
                <ResultGrade>3rd Grade (b)</ResultGrade>
                <ResultValue $highlight>
                  43<ResultPercent>%</ResultPercent>
                </ResultValue>
                <ResultMetric>ECR Growth (Score&nbsp;2+)</ResultMetric>
              </ResultCard>
              <ResultCard>
                <ResultGrade>4th Grade</ResultGrade>
                <ResultValue>
                  25<ResultPercent>%</ResultPercent>
                </ResultValue>
                <ResultMetric>ECR Growth (Score&nbsp;2+)</ResultMetric>
              </ResultCard>
            </ResultsGrid>

            <ResultBlockQuote>
              <QuoteMark style={{ color: theme.colors.orange }}>&ldquo;</QuoteMark>
              <QuoteText style={{ color: theme.colors.white }}>
                One of the first complaints you hear from teachers is, &lsquo;We
                can&apos;t get kids to write.&rsquo; And the second is, &lsquo;How do I
                give feedback to kids in a class period?&rsquo;
              </QuoteText>
              <QuoteAttrib style={{ color: theme.colors.orange }}>
                — Misty Del Angel, Principal
              </QuoteAttrib>
            </ResultBlockQuote>
          </CenteredSection>
        </MaxWidthContainer>
      </ResultsSection>

      {/* WHY IT MATTERS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              WHY IT MATTERS
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              Intentional Implementation, Lasting Change
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              At Charlie Marshall, students aren&apos;t just writing more&nbsp;&mdash;
              they&apos;re starting to believe they can grow. Students celebrate progress
              they hadn&apos;t experienced before, especially those who had previously
              struggled.
            </SectionText>

            <WhyItMattersGrid>
              <WhyItMattersCard>
                <WhyItMattersIcon>🏫</WhyItMattersIcon>
                <WhyItMattersTitle>For School Leaders</WhyItMattersTitle>
                <WhyItMattersText>
                  A proven five-step framework for rolling out new instructional tools
                  with teacher buy-in and measurable results.
                </WhyItMattersText>
              </WhyItMattersCard>
              <WhyItMattersCard>
                <WhyItMattersIcon>🧑‍🏫</WhyItMattersIcon>
                <WhyItMattersTitle>For Teachers</WhyItMattersTitle>
                <WhyItMattersText>
                  Support without pressure&nbsp;&mdash; ready-made lessons, modeling from
                  leadership, and freedom to grow at their own pace.
                </WhyItMattersText>
              </WhyItMattersCard>
              <WhyItMattersCard>
                <WhyItMattersIcon>✍️</WhyItMattersIcon>
                <WhyItMattersTitle>For Students</WhyItMattersTitle>
                <WhyItMattersText>
                  Real-time feedback that builds confidence and excitement around writing
                  improvement.
                </WhyItMattersText>
              </WhyItMattersCard>
            </WhyItMattersGrid>

            <FinalQuoteCard>
              <QuoteMark>&ldquo;</QuoteMark>
              <FinalQuoteText>
                That&apos;s what happens when implementation is intentional. When strong
                leadership meets strong instruction, real change follows.
              </FinalQuoteText>
              <QuoteAttrib>— Charlie Marshall Elementary Impact Story</QuoteAttrib>
            </FinalQuoteCard>

            <TagRow>
              {[
                'Writing',
                'Feedback',
                'Leadership',
                'Teacher Buy-In',
                'Implementation',
              ].map(tag => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </TagRow>

            <GetStartedWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* DOWNLOAD */}
      <DownloadSection>
        <MaxWidthContainer>
          <DownloadInner>
            <DownloadIcon aria-hidden="true">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </DownloadIcon>
            <DownloadText>
              <DownloadTitle>Download the Case Study</DownloadTitle>
              <DownloadSubtitle>
                Get the full PDF version of the Charlie Marshall Elementary case study.
              </DownloadSubtitle>
            </DownloadText>
            <a
              href="/Curipod_Case_Study_Charlie_Marshall_Elementary.pdf"
              download="Curipod_Case_Study_Charlie_Marshall_Elementary.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 48 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadInner>
        </MaxWidthContainer>
      </DownloadSection>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default CharlieMarshallCaseStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 600px;
    height: 600px;
    top: -180px;
    right: -120px;
  }

  &::after {
    width: 400px;
    height: 400px;
    bottom: -80px;
    left: -100px;
  }
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  color: ${theme.colors.white};
  margin: 0 0 12px;
  line-height: 1.1;

  @media ${MOBILE} {
    font-size: 48px;
  }

  @media (max-width: 320px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0 0 20px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
  max-width: 640px;
  line-height: 1.7;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(255, 255, 255, 0.15);
  color: ${theme.colors.white};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const HeroCtaWrapper = styled.div`
  margin-top: 36px;
`;

const HeroCtaButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 48px;
  font-size: 20px;
  height: 58px;
  font-weight: 800;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18), 0 0 0 4px rgba(255, 255, 255, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22), 0 0 0 5px rgba(255, 255, 255, 0.35);
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
  }

  @media ${MOBILE} {
    font-size: 18px;
    height: 52px;
    padding: 0 36px;
  }
`;

const StatBannerWrapper = styled.div`
  background: rgba(0, 0, 0, 0.28);
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 20px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  flex-wrap: nowrap;

  @media ${MOBILE} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const StatDivider = styled.div`
  width: 1px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 28px;
  justify-self: center;
  flex-shrink: 0;

  @media ${MOBILE} {
    width: 60px;
    height: 1px;
    margin: 0 auto;
  }
`;

const StatItem = styled.div<{ $highlight?: boolean }>`
  text-align: center;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};
  border: ${({ $highlight }) =>
    $highlight ? '1px solid rgba(255, 255, 255, 0.4)' : 'none'};
`;

const StatNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: ${theme.colors.white};
  line-height: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @media ${MOBILE} {
    font-size: 44px;
  }
`;

const StatPercent = styled.span`
  font-size: 24px;
  margin-top: 6px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
`;

const StatYear = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.82);
  margin-top: 2px;
`;

const ArrowContainer = styled.div`
  font-size: 36px;
  color: ${theme.colors.white};
  font-weight: bold;

  @media ${MOBILE} {
    font-size: 24px;
  }
`;

/* ─── DOWNLOAD CTA BANNER (below hero) ──────────────────────────────────────── */

const DownloadCtaBanner = styled.div`
  background: ${theme.colors.beigeDark};
  border-bottom: 1px solid rgba(249, 151, 70, 0.2);
  padding: 16px 0;
`;

const DownloadCtaInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
`;

const DownloadCtaLabel = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${theme.colors.text.dark};

  @media ${MOBILE} {
    font-size: 14px;
  }
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTag = styled.span`
  display: inline-block;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── CHALLENGE SECTION ──────────────────────────────────────────────────────── */

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const BlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 32px 0 0;
  background: rgba(249, 151, 70, 0.08);
  border-radius: 0 10px 10px 0;
`;

const QuoteMark = styled.span`
  display: block;
  font-size: 48px;
  color: ${theme.colors.orange};
  line-height: 0.8;
  margin-bottom: 8px;
`;

const QuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 12px;

  @media ${MOBILE} {
    font-size: 18px;
  }
`;

const QuoteAttrib = styled.cite`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

const TeamCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.magicFloatingShadow};

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const TeamCardTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${theme.colors.orange};
`;

const TeamMemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TeamMember = styled.div``;

const TeamRole = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const TeamName = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${theme.colors.black};
`;

const TeamNote = styled.div`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  margin-top: 2px;
`;

/* ─── FOCUS AREAS ────────────────────────────────────────────────────────────── */

const FocusAreaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const FocusAreaBadge = styled.div`
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 15px;
  font-weight: bold;
  padding: 10px 24px;
  border-radius: 30px;
`;

/* ─── IMPACT GRID ────────────────────────────────────────────────────────────── */

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 40px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ImpactCard = styled.div<{ $wide?: boolean }>`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.defaultShadow};
  border-top: 4px solid ${theme.colors.orange};
  transition: ${theme.effects.defaultTransition};

  ${({ $wide }) =>
    $wide &&
    css`
      grid-column: 1 / -1;
    `}

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-2px);
  }

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const ImpactIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const ImpactTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 16px;
`;

const ImpactQuote = styled.p`
  font-size: 16px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const ImpactText = styled.p`
  font-size: 15px;
  color: ${theme.colors.text.dark};
  line-height: 1.6;
  margin-bottom: 8px;
`;

const ImpactAttrib = styled.cite`
  font-size: 13px;
  font-weight: bold;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

/* ─── RESULTS SECTION ────────────────────────────────────────────────────────── */

const ResultsSection = styled.section`
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  padding: 80px 0;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 500px;
    height: 500px;
    top: -150px;
    right: -100px;
  }

  &::after {
    width: 350px;
    height: 350px;
    bottom: -80px;
    left: -80px;
  }

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  width: 100%;
  max-width: 720px;
  margin-bottom: 48px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ResultCard = styled.div<{ $highlight?: boolean }>`
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(249, 151, 70, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid
    ${({ $highlight }) =>
      $highlight ? 'rgba(249, 151, 70, 0.4)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 16px;
  padding: 28px 20px;
  text-align: center;
`;

const ResultGrade = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ResultValue = styled.div<{ $highlight?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: ${({ $highlight }) => ($highlight ? theme.colors.orange : theme.colors.white)};
  line-height: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @media ${MOBILE} {
    font-size: 44px;
  }
`;

const ResultPercent = styled.span`
  font-size: 24px;
  margin-top: 6px;
`;

const ResultMetric = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
  margin-top: 8px;
`;

const ResultBlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 0 0 48px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0 10px 10px 0;
  max-width: 640px;
  width: 100%;
`;

/* ─── WHY IT MATTERS ─────────────────────────────────────────────────────────── */

const WhyItMattersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  width: 100%;
  margin-bottom: 48px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 32px;
  }
`;

const WhyItMattersCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 28px 24px;
  text-align: center;
  box-shadow: ${theme.effects.defaultShadow};

  @media ${MOBILE} {
    padding: 20px 16px;
  }
`;

const WhyItMattersIcon = styled.div`
  font-size: 28px;
  margin-bottom: 12px;
`;

const WhyItMattersTitle = styled.h3`
  font-size: 17px;
  font-weight: bold;
  color: ${theme.colors.black};
  margin-bottom: 8px;
`;

const WhyItMattersText = styled.p`
  font-size: 15px;
  color: ${theme.colors.text.dark};
  line-height: 1.6;
  margin: 0;
`;

const FinalQuoteCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 40px 48px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 28px 20px;
  }
`;

const FinalQuoteText = styled.p`
  font-size: 28px;
  font-weight: 700;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.4;
  margin: 0 0 16px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const Tag = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 14px;
  font-weight: bold;
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const GetStartedWrapper = styled.div`
  margin-top: 40px;
`;

/* ─── DOWNLOAD ───────────────────────────────────────────────────────────────── */

const DownloadSection = styled.section`
  background-color: ${theme.colors.beigeDark};
  border-top: 1px solid rgba(249, 151, 70, 0.25);
  padding: 32px 0;
`;

const DownloadInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
  }
`;

const DownloadIcon = styled.div`
  color: ${theme.colors.orangeDark};
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media ${MOBILE} {
    display: none;
  }
`;

const DownloadText = styled.div`
  flex: 1;
`;

const DownloadTitle = styled.p`
  font-size: 17px;
  font-weight: bold;
  color: ${theme.colors.black};
  margin: 0 0 2px;
`;

const DownloadSubtitle = styled.p`
  font-size: 14px;
  color: ${theme.colors.text.dark};
  font-weight: 400;
  margin: 0;
`;
